import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import Button from 'react-bootstrap/Button';
import { AiOutlineEdit, AiOutlineDelete } from 'react-icons/ai';
import { LuMousePointerClick } from 'react-icons/lu';

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1rem;
	background: rgba(${({ theme }) => theme.colors.borderLightRgb}, 0.1);
	border-radius: 0.375rem;
`;

const SectionLabel = styled.h4`
	margin: 0 0 0.5rem 0;
	font-size: 1rem;
	font-weight: 600;
`;

const Description = styled.p`
	margin: 0 0 1rem 0;
	font-size: 0.875rem;
	color: rgba(255, 255, 255, 0.7);
`;

const ButtonGroup = styled.div`
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
	align-items: center;

	.btn {
		display: inline-flex;
		align-items: center;
		gap: 0.25rem;
	}
`;

const ColorPickerWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
`;

const ColorInput = styled.input`
	width: 40px;
	height: 38px;
	border: 2px solid rgba(255, 255, 255, 0.2);
	border-radius: 0.375rem;
	cursor: pointer;
	background: transparent;

	&::-webkit-color-swatch-wrapper {
		padding: 0;
	}

	&::-webkit-color-swatch {
		border: none;
		border-radius: 0.25rem;
	}
`;

const ColorLabel = styled.span`
	font-size: 0.875rem;
	color: rgba(255, 255, 255, 0.7);
`;

const BottomRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 0.5rem;
`;

const CoordInfo = styled.div`
	display: flex;
	align-items: center;
	gap: 0.25rem;
	font-size: 0.75rem;

	strong {
		color: rgba(255, 255, 255, 0.7);
	}

	span {
		color: #ffffff;
		font-family: monospace;
	}
`;

const Hint = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 0.25rem;
	font-size: 0.75rem;
	color: ${({ theme }) => theme.colors.green};
`;

const VirtualLineControls = ({
	drawMode = false,
	onStartDrawing,
	onClearLine,
	startPoint = null,
	lineColor = '#198754',
	onLineColorChange,
}) => {
	const { watch } = useFormContext();
	const virtualLineDefinition = watch('virtualLineDefinition');

	const hasLine = !!virtualLineDefinition;
	const hasStartPoint = !!startPoint;

	const handleDrawLine = () => {
		onStartDrawing?.();
	};

	const handleRemoveLine = () => {
		onClearLine?.();
	};

	const handleColorChange = e => {
		onLineColorChange?.(e.target.value);
	};

	const getInstructionText = () => {
		if (!drawMode) {
			return 'Click "Draw Line" then click on the video to define the counting line.';
		}
		if (!hasStartPoint) {
			return 'Click on the video to set the start point.';
		}
		return 'Click once more to set the end point.';
	};

	return (
		<Container>
			<div>
				<SectionLabel>Virtual Line Definition</SectionLabel>
				<Description>{getInstructionText()}</Description>
			</div>

			<ButtonGroup>
				<Button variant="success" onClick={handleDrawLine} disabled={drawMode}>
					<AiOutlineEdit aria-hidden="true" />
					{drawMode ? 'Drawing...' : 'Draw Line'}
				</Button>

				<Button
					variant="danger"
					onClick={handleRemoveLine}
					disabled={!hasLine && !drawMode}>
					<AiOutlineDelete aria-hidden="true" />
					Remove Line
				</Button>

				{drawMode && !virtualLineDefinition && (
					<Hint>
						<LuMousePointerClick size={14} aria-hidden="true" />
						{hasStartPoint ? 'Click to set end point' : 'Click to set start point'}
					</Hint>
				)}
			</ButtonGroup>

			<BottomRow>
				<ColorPickerWrapper>
					<ColorLabel>Line Color:</ColorLabel>
					<ColorInput
						type="color"
						value={lineColor}
						onChange={handleColorChange}
						title="Choose line color"
					/>
				</ColorPickerWrapper>
				{virtualLineDefinition ? (
					<>
						<CoordInfo>
							<strong>Start:</strong>
							<span>
								[{virtualLineDefinition.start_point[0]},{' '}
								{virtualLineDefinition.start_point[1]}]
							</span>
						</CoordInfo>
						<CoordInfo>
							<strong>End:</strong>
							<span>
								[{virtualLineDefinition.end_point[0]},{' '}
								{virtualLineDefinition.end_point[1]}]
							</span>
						</CoordInfo>
					</>
				) : (
					<div />
				)}
			</BottomRow>
		</Container>
	);
};

export default VirtualLineControls;
